import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';

export interface ImageUploadResult {
  success: boolean;
  url?: string;
  sessionId?: string;
  error?: string;
  original_filename?: string;
  converted_filename?: string;
  original_format?: string;
  original_size?: number;
  final_size?: number;
  compression_ratio?: string;
  conversion?: {
    converted: boolean;
    original_format: string;
    converted_format: string;
    original_size: number;
    converted_size: number;
    compression_ratio: string;
  };
  vision_processing?: {
    processed: boolean;
    faces_detected: number;
    license_plates_detected: number;
  };
  compressed?: boolean;
  originalSize?: number;
  compressedSize?: number;
}

export interface ImageValidationResult {
  valid: boolean;
  errors: string[];
  warnings?: string[];
}

export interface UploadedImage {
  id: string;
  file: File;
  url: string;
  preview: string;
  uploading: boolean;
  uploaded: boolean;
  error?: string;
  sessionId?: string;
  compressed?: boolean;
  originalSize?: number;
  compressedSize?: number;
  conversion?: {
    converted: boolean;
    original_format: string;
    converted_format: string;
    original_size: number;
    converted_size: number;
    compression_ratio: string;
  };
  retryCount?: number;
  isHEIC?: boolean;
  uploadProgress?: number;
}

@Injectable({
  providedIn: 'root',
})
export class ImageUploadService {
  private readonly MAX_FILE_SIZE = 20 * 1024 * 1024;
  private readonly MAX_IMAGES = 42;
  private readonly MAX_RETRIES = 2;
  private readonly UPLOAD_TIMEOUT = 120000;
  private readonly BATCH_SIZE = 10;

  private readonly ALLOWED_TYPES = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/webp',
    'image/gif',
    'image/bmp',
    'image/tiff',
    'image/tif',
    'image/avif',
    'image/heic',
    'image/heif',
  ];

  private readonly ALLOWED_EXTENSIONS = [
    '.jpg',
    '.jpeg',
    '.png',
    '.webp',
    '.gif',
    '.bmp',
    '.tiff',
    '.tif',
    '.avif',
    '.heic',
    '.heif',
    '.svg',
  ];

  private apiUrl = environment.apiUrl || 'http://localhost:3000/api/v1';
  private currentSessionId: string | null = null;
  private compressionEnabled = false;
  private blobUrls: Set<string> = new Set();
  private abortControllers = new Map<string, AbortController>();

  constructor(private http: HttpClient, private authService: AuthService) {}

  validateImages(files: File[]): ImageValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (files.length === 0) {
      return { valid: true, errors: [], warnings: [] };
    }

    if (files.length > this.MAX_IMAGES) {
      errors.push(
        `Maximum ${this.MAX_IMAGES} images allowed. Please remove ${
          files.length - this.MAX_IMAGES
        } images.`
      );
    }

    const seenFiles = new Map<string, number>();

    files.forEach((file, index) => {
      const fileKey = `${file.name}-${file.size}`;
      if (seenFiles.has(fileKey)) {
        warnings.push(
          `Image ${index + 1} "${
            file.name
          }": Duplicate file detected (same as image ${
            seenFiles.get(fileKey)! + 1
          })`
        );
      } else {
        seenFiles.set(fileKey, index);
      }

      if (!this.ALLOWED_TYPES.includes(file.type)) {
        errors.push(
          `Image ${index + 1} "${file.name}": Unsupported format "${
            file.type
          }". Supported: JPEG, PNG, WebP, GIF, BMP, TIFF, AVIF, HEIC, SVG`
        );
      }

      const fileName = file.name.toLowerCase();
      const hasValidExtension = this.ALLOWED_EXTENSIONS.some((ext) =>
        fileName.endsWith(ext.toLowerCase())
      );

      if (!hasValidExtension) {
        errors.push(
          `Image ${index + 1} "${file.name}": File extension not supported`
        );
      }

      if (file.size > this.MAX_FILE_SIZE) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
        const maxSizeMB = (this.MAX_FILE_SIZE / (1024 * 1024)).toFixed(0);

        warnings.push(
          `Image ${index + 1} "${
            file.name
          }": ${sizeMB}MB exceeds ${maxSizeMB}MB limit. Backend will optimize it automatically.`
        );
      }

      if (file.name.length > 100) {
        errors.push(
          `Image ${index + 1}: Filename too long (max 100 characters)`
        );
      }

      if (file.size === 0) {
        errors.push(`Image ${index + 1} "${file.name}": File is empty`);
      }

      if (this.isHEICFormat(file.type)) {
        warnings.push(
          `Image ${index + 1} "${
            file.name
          }": HEIC format will be converted to WebP automatically by the server`
        );
      }
    });

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  }

  async compressImage(file: File): Promise<File> {
    return file;
  }

  async compressImages(files: File[]): Promise<File[]> {
    return files;
  }

  private isCompressibleFormat(mimeType: string): boolean {
    return [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
      'image/bmp',
      'image/tiff',
      'image/heic',
      'image/heif',
    ].includes(mimeType);
  }

  generateNewSession(): string {
    this.currentSessionId = `listing_${Date.now()}_${Math.random()
      .toString(36)
      .substring(7)}`;
    return this.currentSessionId;
  }

  getCurrentSessionId(): string | null {
    return this.currentSessionId;
  }

  clearSession(): void {
    this.currentSessionId = null;
  }

  createImagePreviews(files: File[]): UploadedImage[] {
    return files.map((file) => {
      const isHEIC = this.isHEICFormat(file.type);
      const id = `${file.name}-${file.size}-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      let preview: string;

      if (isHEIC) {
        preview = this.getHEICPlaceholder();
      } else {
        preview = URL.createObjectURL(file);
        this.blobUrls.add(preview);
      }

      return {
        id,
        file,
        url: '',
        preview,
        uploading: false,
        uploaded: false,
        originalSize: file.size,
        retryCount: 0,
        isHEIC: isHEIC,
      };
    });
  }

  private getHEICPlaceholder(): string {
    return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300"%3E%3Crect width="400" height="300" fill="%23f3f4f6"/%3E%3Cg transform="translate(200,150)"%3E%3Ccircle cx="0" cy="-20" r="30" fill="%239ca3af" opacity="0.5"/%3E%3Cpath d="M-40,20 L-20,0 L20,40 L40,20 L40,60 L-40,60 Z" fill="%239ca3af" opacity="0.5"/%3E%3C/g%3E%3Ctext x="200" y="200" text-anchor="middle" font-family="Arial, sans-serif" font-size="16" fill="%236b7280"%3EHEIC Image%3C/text%3E%3Ctext x="200" y="220" text-anchor="middle" font-family="Arial, sans-serif" font-size="12" fill="%239ca3af"%3EPreview after upload%3C/text%3E%3C/svg%3E';
  }

  private getAuthHeaders(): HttpHeaders {
    const token = this.authService.getToken();
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
  }

  isRetryableError(error: any): boolean {
    return (
      error.status === 504 ||
      error.status === 502 ||
      error.status === 503 ||
      error.status === 408 ||
      error.status === 0 ||
      error.message?.includes('timeout') ||
      error.message?.includes('connection')
    );
  }

  private async delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private getRetryDelay(retryCount: number): number {
    return Math.min(1000 * Math.pow(2, retryCount), 8000);
  }

  async uploadListingImages(
    images: UploadedImage[],
    sessionId?: string
  ): Promise<{ urls: string[]; errors: string[] }> {
    try {
      const urls = await this.uploadImages(images, sessionId);
      const errors = images
        .filter((img) => !img.uploaded && img.error)
        .map((img) => img.error || 'Unknown error');

      return { urls, errors };
    } catch (error: any) {
      const urls = images.filter((img) => img.uploaded).map((img) => img.url);
      const errors = images
        .filter((img) => !img.uploaded)
        .map((img) => img.error || 'Upload failed');

      return { urls, errors };
    }
  }

  async uploadImages(
    images: UploadedImage[],
    sessionId?: string
  ): Promise<string[]> {
    const uploadSessionId = sessionId || this.generateNewSession();
    const results: string[] = [];

    for (let i = 0; i < images.length; i += this.BATCH_SIZE) {
      const batch = images.slice(i, i + this.BATCH_SIZE);

      const batchPromises = batch.map(async (imageData) => {
        imageData.uploading = true;
        imageData.uploaded = false;
        imageData.error = undefined;

        try {
          const result = await this.uploadImage(
            imageData.file,
            uploadSessionId,
            true
          );

          if (result.success && result.url) {
            imageData.url = result.url;
            imageData.uploaded = true;
            imageData.uploading = false;
            imageData.sessionId = result.sessionId;

            if (result.conversion) {
              imageData.conversion = result.conversion;
            }

            return result.url;
          } else {
            throw new Error(result.error || 'Upload failed');
          }
        } catch (error: any) {
          imageData.uploaded = false;
          imageData.uploading = false;
          imageData.error = error.message || 'Upload failed';
          imageData.retryCount = (imageData.retryCount || 0) + 1;
          throw error;
        }
      });

      try {
        const batchResults = await Promise.allSettled(batchPromises);

        batchResults.forEach((result) => {
          if (result.status === 'fulfilled') {
            results.push(result.value);
          }
        });
      } catch (error) {
        console.error('Batch upload error:', error);
      }
    }

    return results;
  }

  async uploadImage(
    file: File,
    sessionId?: string,
    enableVisionProcessing: boolean = true
  ): Promise<ImageUploadResult> {
    return this.uploadImageWithRetry(
      file,
      sessionId,
      enableVisionProcessing,
      0
    );
  }

  private async uploadImageWithRetry(
    file: File,
    sessionId: string | undefined,
    enableVisionProcessing: boolean,
    retryCount: number
  ): Promise<ImageUploadResult> {
    const uploadId = `${file.name}-${Date.now()}`;
    const controller = new AbortController();
    this.abortControllers.set(uploadId, controller);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const uploadSessionId = sessionId || this.currentSessionId;
      if (uploadSessionId) {
        formData.append('sessionId', uploadSessionId);
      }

      formData.append(
        'enableVisionProcessing',
        enableVisionProcessing.toString()
      );

      const headers = this.getAuthHeaders();

      const response = await this.http
        .post<ImageUploadResult>(`${this.apiUrl}/upload/image`, formData, {
          headers,
        })
        .toPromise();

      if (!response) {
        throw new Error('Empty response from server');
      }

      if (response.success) {
        if (response.sessionId) {
          this.currentSessionId = response.sessionId;
        }
        this.abortControllers.delete(uploadId);
        return response;
      } else {
        throw new Error(response.error || 'Upload failed');
      }
    } catch (error: any) {
      this.abortControllers.delete(uploadId);

      if (!error.status && !error.message) {
        throw new Error('Network error: Unable to reach server');
      }

      if (this.isRetryableError(error) && retryCount < this.MAX_RETRIES) {
        await this.delay(this.getRetryDelay(retryCount));
        return this.uploadImageWithRetry(
          file,
          sessionId,
          enableVisionProcessing,
          retryCount + 1
        );
      }

      throw error;
    }
  }

  cancelUpload(uploadId: string): void {
    const controller = this.abortControllers.get(uploadId);
    if (controller) {
      controller.abort();
      this.abortControllers.delete(uploadId);
    }
  }

  cancelAllUploads(): void {
    this.abortControllers.forEach((controller) => controller.abort());
    this.abortControllers.clear();
  }

  async uploadMultipleImages(
    images: UploadedImage[],
    sessionId?: string,
    enableVisionProcessing: boolean = true,
    onProgress?: (uploaded: number, total: number) => void
  ): Promise<UploadedImage[]> {
    const results: UploadedImage[] = [...images];
    const uploadSessionId = sessionId || this.generateNewSession();
    const filesToUpload = images.map((img) => img.file);

    for (let i = 0; i < filesToUpload.length; i += this.BATCH_SIZE) {
      const batch = filesToUpload.slice(i, i + this.BATCH_SIZE);

      const batchPromises = batch.map((file, batchIdx) => {
        const imageIndex = i + batchIdx;
        results[imageIndex].uploading = true;

        return this.uploadImage(file, uploadSessionId, enableVisionProcessing)
          .then((result) => {
            results[imageIndex].uploaded = true;
            results[imageIndex].uploading = false;
            results[imageIndex].url = result.url || '';
            results[imageIndex].conversion = result.conversion;
            results[imageIndex].sessionId = result.sessionId;

            if (onProgress) {
              const uploadedCount = results.filter((r) => r.uploaded).length;
              onProgress(uploadedCount, results.length);
            }

            return result;
          })
          .catch((error) => {
            results[imageIndex].uploaded = false;
            results[imageIndex].uploading = false;
            results[imageIndex].error =
              error.error?.error || error.message || 'Upload failed';

            if (onProgress) {
              const uploadedCount = results.filter((r) => r.uploaded).length;
              onProgress(uploadedCount, results.length);
            }

            throw error;
          });
      });

      await Promise.allSettled(batchPromises);
    }

    return results;
  }

  async uploadMultipleImagesAtOnce(
    files: File[],
    sessionId?: string,
    enableVisionProcessing: boolean = true
  ): Promise<{
    urls: string[];
    errors: string[];
    sessionId?: string;
    summary?: any;
  }> {
    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('images', file);
      });

      const uploadSessionId = sessionId || this.currentSessionId;
      if (uploadSessionId) {
        formData.append('sessionId', uploadSessionId);
      }

      formData.append(
        'enableVisionProcessing',
        enableVisionProcessing.toString()
      );

      const headers = this.getAuthHeaders();

      const response = await this.http
        .post<{
          success: boolean;
          urls?: string[];
          error?: string;
          sessionId?: string;
          summary?: any;
        }>(`${this.apiUrl}/upload/images`, formData, { headers })
        .toPromise();

      if (!response) {
        return {
          urls: [],
          errors: ['Empty response from server'],
        };
      }

      if (response.success && response.urls) {
        if (response.sessionId) {
          this.currentSessionId = response.sessionId;
        }

        return {
          urls: response.urls,
          errors: [],
          sessionId: response.sessionId,
          summary: response.summary,
        };
      } else {
        return {
          urls: [],
          errors: [response.error || 'Upload failed'],
          sessionId: response.sessionId,
        };
      }
    } catch (error: any) {
      console.error('Batch upload error:', error);

      let errorMessage = 'Upload failed';
      if (error.error?.error) {
        errorMessage = error.error.error;
      } else if (error.error?.message) {
        errorMessage = error.error.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        urls: [],
        errors: [errorMessage],
      };
    }
  }

  cleanupPreviews(images: UploadedImage[]): void {
    images.forEach((image) => {
      if (image.preview && image.preview.startsWith('blob:')) {
        URL.revokeObjectURL(image.preview);
        this.blobUrls.delete(image.preview);
      }
    });
  }

  cleanupAllBlobs(): void {
    this.blobUrls.forEach((url) => {
      URL.revokeObjectURL(url);
    });
    this.blobUrls.clear();
  }

  getSupportedFormatsForDisplay(): string {
    return 'JPEG, PNG, WebP, GIF, BMP, TIFF, AVIF, HEIC (iPhone), SVG';
  }

  getSupportedFormatsDetailed(): string {
    return `Supported formats:
• JPEG/JPG - Standard photos
• PNG - Images with transparency
• WebP - Modern web format
• GIF - Animated images
• BMP - Windows bitmap
• TIFF - High-quality images
• AVIF - Next-gen format
• HEIC/HEIF - iPhone photos (iOS 11+)
• SVG - Vector graphics

All images are automatically converted to WebP by the server for optimal compression and quality.
Max size: 20MB per image | Max images: 42`;
  }

  getAcceptAttribute(): string {
    return this.ALLOWED_EXTENSIONS.join(',');
  }

  getSupportedExtensions(): string[] {
    return [...this.ALLOWED_EXTENSIONS];
  }

  getSupportedMimeTypes(): string[] {
    return [...this.ALLOWED_TYPES];
  }

  hasLimitedBrowserSupport(mimeType: string): boolean {
    const limitedSupportFormats = [
      'image/heic',
      'image/heif',
      'image/avif',
      'image/bmp',
      'image/tiff',
    ];
    return limitedSupportFormats.includes(mimeType);
  }

  getBrowserCompatibilityWarning(mimeType: string): string | null {
    const warnings: { [key: string]: string } = {
      'image/heic':
        '✓ HEIC format (iPhone) will be converted to WebP automatically on the server',
      'image/heif':
        '✓ HEIF format will be converted to WebP automatically on the server',
      'image/avif':
        '✓ AVIF format will be converted to WebP for compatibility on the server',
      'image/bmp':
        '✓ BMP format will be converted to WebP and optimized on the server',
      'image/tiff':
        '✓ TIFF format will be converted to WebP and optimized on the server',
    };
    return warnings[mimeType] || null;
  }

  isHEICFormat(mimeType: string): boolean {
    return mimeType === 'image/heic' || mimeType === 'image/heif';
  }

  getFormatDisplayName(mimeType: string): string {
    const formatNames: { [key: string]: string } = {
      'image/jpeg': 'JPEG',
      'image/jpg': 'JPEG',
      'image/png': 'PNG',
      'image/webp': 'WebP',
      'image/gif': 'GIF',
      'image/bmp': 'BMP',
      'image/tiff': 'TIFF',
      'image/tif': 'TIFF',
      'image/avif': 'AVIF',
      'image/heic': 'HEIC',
      'image/heif': 'HEIF',
    };

    return formatNames[mimeType] || mimeType.split('/')[1].toUpperCase();
  }

  isValidImageType(mimeType: string): boolean {
    return this.ALLOWED_TYPES.includes(mimeType);
  }

  isValidImageExtension(filename: string): boolean {
    const lowercaseName = filename.toLowerCase();
    return this.ALLOWED_EXTENSIONS.some((ext) =>
      lowercaseName.endsWith(ext.toLowerCase())
    );
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  isFileSizeValid(fileSize: number): boolean {
    return fileSize <= this.MAX_FILE_SIZE && fileSize > 0;
  }

  getMaxFileSizeMB(): number {
    return this.MAX_FILE_SIZE / (1024 * 1024);
  }

  hasActiveSession(): boolean {
    return this.currentSessionId !== null;
  }

  getSessionInfo(): { sessionId: string | null; hasSession: boolean } {
    return {
      sessionId: this.currentSessionId,
      hasSession: this.hasActiveSession(),
    };
  }

  isNetworkError(error: any): boolean {
    return (
      error.name === 'NetworkError' ||
      error.message?.includes('fetch') ||
      error.status === 0
    );
  }

  isAuthError(error: any): boolean {
    return error.status === 401 || error.status === 403;
  }

  isValidationError(error: any): boolean {
    return error.status === 400;
  }

  getErrorCategory(
    error: any
  ): 'network' | 'auth' | 'validation' | 'server' | 'timeout' | 'unknown' {
    if (this.isRetryableError(error)) return 'timeout';
    if (this.isNetworkError(error)) return 'network';
    if (this.isAuthError(error)) return 'auth';
    if (this.isValidationError(error)) return 'validation';
    if (error.status >= 500) return 'server';
    return 'unknown';
  }

  getFormatStatistics(images: UploadedImage[]): {
    totalImages: number;
    formatCounts: { [format: string]: number };
    convertedImages: number;
    totalOriginalSize: number;
    totalFinalSize: number;
    totalSavings: number;
    savingsPercentage: string;
  } {
    const stats = {
      totalImages: images.length,
      formatCounts: {} as { [format: string]: number },
      convertedImages: 0,
      totalOriginalSize: 0,
      totalFinalSize: 0,
      totalSavings: 0,
      savingsPercentage: '0',
    };

    images.forEach((image) => {
      const format = this.getFormatDisplayName(image.file.type);
      stats.formatCounts[format] = (stats.formatCounts[format] || 0) + 1;

      stats.totalOriginalSize += image.originalSize || image.file.size;

      if (image.conversion) {
        stats.convertedImages++;
        stats.totalFinalSize += image.conversion.converted_size;
      } else {
        stats.totalFinalSize += image.file.size;
      }
    });

    stats.totalSavings = stats.totalOriginalSize - stats.totalFinalSize;
    stats.savingsPercentage =
      stats.totalOriginalSize > 0
        ? ((stats.totalSavings / stats.totalOriginalSize) * 100).toFixed(1)
        : '0';

    return stats;
  }

  setCompressionEnabled(enabled: boolean): void {
    this.compressionEnabled = enabled;
  }

  isCompressionEnabled(): boolean {
    return this.compressionEnabled;
  }
}
